import { useEffect, useMemo, useRef, useState } from "react";
import { Search, MapPin, Calendar, ChevronDown, Briefcase } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { trackFunnelEvent } from "@/lib/funnelEvents";
import { DateRange } from "react-day-picker";

interface SearchBarProps {
  isCompact?: boolean;
  onSearch?: (filters: SearchFilters) => void;
  cidades?: string[];
  servicos?: { id: number; nome: string }[];
  initialService?: string;
  showServiceSelector?: boolean;
}

export interface SearchFilters {
  cidade: string;
  dates: string;
  dateRange?: DateRange;
  service: string;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(query.matches);

    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return isMobile;
}

export default function SearchBar({
  isCompact = false,
  onSearch,
  cidades = [],
  servicos = [],
  initialService = "",
  showServiceSelector = false,
}: SearchBarProps) {
  const isMobile = useIsMobile();

  const [filters, setFilters] = useState<SearchFilters>({
    cidade: "",
    dates: "",
    dateRange: undefined,
    service: initialService,
  });
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [locationOpen, setLocationOpen] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");
  const [serviceOpen, setServiceOpen] = useState(false);
  const [serviceQuery, setServiceQuery] = useState("");
  const [dateOpen, setDateOpen] = useState(false);
  const calendarScrollRef = useRef<HTMLDivElement | null>(null);

  const resetCalendarScroll = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        calendarScrollRef.current?.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      });
    });
  };

  const handleDateSelect = (range: DateRange | undefined) => {
    setDateRange(range);

    if (range?.from) {
      const fromStr = format(range.from, "dd MMM", { locale: ptBR });
      const toStr = range.to ? format(range.to, "dd MMM", { locale: ptBR }) : "";

      setFilters((prev) => ({
        ...prev,
        dates: toStr ? `${fromStr} - ${toStr}` : fromStr,
        dateRange: range,
      }));
      return;
    }

    setFilters((prev) => ({
      ...prev,
      dates: "",
      dateRange: undefined,
    }));
  };

  const cidadesFiltradas = useMemo(() => {
    const query = locationQuery.trim().toLowerCase();
    if (!query) return cidades;
    return cidades.filter((cidade) => cidade.toLowerCase().includes(query));
  }, [cidades, locationQuery]);

  const servicosFiltrados = useMemo(() => {
    const query = serviceQuery.trim().toLowerCase();
    if (!query) return servicos;
    return servicos.filter((servico) => servico.nome.toLowerCase().includes(query));
  }, [servicos, serviceQuery]);

  const handleLocationPick = (cidade: string) => {
    setFilters((prev) => ({ ...prev, cidade }));
    setLocationQuery(cidade);
    setLocationOpen(false);
  };

  const handleServicePick = (service: string) => {
    setFilters((prev) => ({ ...prev, service }));
    setServiceQuery(service);
    setServiceOpen(false);
  };

  const handleSearch = () => {
    trackFunnelEvent("search_submit", {
      compact: isCompact,
      has_city: Boolean(filters.cidade),
      has_dates: Boolean(filters.dates),
      has_service: Boolean(filters.service),
    });
    onSearch?.(filters);
  };

  if (isCompact) {
    return (
      <div className="bg-background rounded-full border shadow-search hover:shadow-card transition-all cursor-pointer">
        <div className="flex items-center gap-2 px-4 py-2">
          <Search className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium truncate">
            {filters.cidade || "Onde?"} · {filters.dates || "Quando?"}
          </span>
          <Button
            type="button"
            size="sm"
            onClick={handleSearch}
            aria-label="Buscar"
            className="rounded-full bg-coral hover:bg-coral-dark ml-auto"
          >
            <Search className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-background rounded-2xl md:rounded-full border-2 border-border shadow-search hover:shadow-card transition-all p-2">
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-0">
          <Popover
            open={locationOpen}
            onOpenChange={(open) => {
              setLocationOpen(open);
              if (open) setLocationQuery("");
            }}
          >
            <PopoverTrigger asChild>
              <div className="flex-1 px-4 py-2 md:border-r border-border cursor-pointer hover:bg-muted/50 rounded-lg transition-colors">
                <label className="block text-xs font-bold text-foreground mb-1">Onde?</label>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className={cn("text-sm truncate", filters.cidade ? "text-foreground" : "text-muted-foreground")}>
                    {filters.cidade || "Escolha a cidade"}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground ml-auto" />
                </div>
              </div>
            </PopoverTrigger>

            <PopoverContent className="w-[320px] p-3" align="start">
              <h3 className="font-bold text-sm mb-2">Escolha a cidade</h3>
              <input
                type="text"
                value={locationQuery}
                onChange={(event) => setLocationQuery(event.target.value)}
                placeholder="Digite a cidade"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none"
                autoFocus
              />
              <div className="mt-2 max-h-64 overflow-y-auto">
                {cidadesFiltradas.length > 0 ? (
                  <div className="space-y-1">
                    {cidadesFiltradas.map((cidade) => (
                      <button
                        key={cidade}
                        type="button"
                        onClick={() => handleLocationPick(cidade)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm"
                      >
                        {cidade}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Nenhuma cidade encontrada
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {showServiceSelector && (
            <Popover open={serviceOpen} onOpenChange={setServiceOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex-1 px-4 py-2 md:border-r border-border hover:bg-muted/50 rounded-lg transition-colors text-left"
                >
                  <label className="block text-xs font-bold text-foreground mb-1 cursor-pointer">Serviço</label>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Briefcase className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className={cn("text-sm truncate", filters.service ? "text-foreground" : "text-muted-foreground")}>
                        {filters.service || "Selecione o serviço"}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                </button>
              </PopoverTrigger>

              <PopoverContent className="w-[320px] p-2" align="start">
                <div className="space-y-2">
                  <Input
                    autoFocus
                    placeholder="Buscar serviço..."
                    value={serviceQuery}
                    onChange={(event) => setServiceQuery(event.target.value)}
                  />
                  <div className="max-h-64 overflow-auto rounded-md border">
                    {servicosFiltrados.length > 0 ? (
                      servicosFiltrados.map((servico) => (
                        <button
                          key={servico.id}
                          type="button"
                          onClick={() => handleServicePick(servico.nome)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                        >
                          {servico.nome}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        Nenhum serviço encontrado.
                      </div>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          <Popover
            open={dateOpen}
            onOpenChange={(open) => {
              setDateOpen(open);
              if (open && isMobile) resetCalendarScroll();
            }}
          >
            <PopoverTrigger asChild>
              <div className="flex-1 px-4 py-2 cursor-pointer hover:bg-muted/50 rounded-lg transition-colors">
                <label className="block text-xs font-bold text-foreground mb-1">Quando?</label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className={cn("text-sm", filters.dates ? "text-foreground" : "text-muted-foreground")}>
                    {filters.dates || "Selecione as datas"}
                  </span>
                </div>
              </div>
            </PopoverTrigger>

            <PopoverContent
              ref={calendarScrollRef}
              className="relative top-3 h-[70dvh] w-[calc(100vw-2rem)] max-w-[336px] overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y rounded-3xl border-none p-3 shadow-2xl sm:top-0 sm:h-auto sm:max-h-[calc(100dvh-1.5rem)] sm:w-auto sm:max-w-none sm:p-4"
              align="center"
              side="bottom"
              sideOffset={8}
              collisionPadding={16}
              onOpenAutoFocus={(event) => {
                if (!isMobile) return;
                event.preventDefault();
                resetCalendarScroll();
              }}
            >
              <CalendarComponent
                mode="range"
                selected={dateRange}
                onSelect={handleDateSelect}
                numberOfMonths={isMobile ? 12 : 2}
                pagedNavigation={!isMobile}
                hideNavigation={isMobile}
                locale={ptBR}
                weekStartsOn={1}
                className="mx-auto w-fit"
              />
            </PopoverContent>
          </Popover>

          <Button
            onClick={handleSearch}
            className="rounded-full bg-coral hover:bg-coral-dark text-white px-6 py-6 md:ml-2"
          >
            <Search className="w-5 h-5 md:mr-2" />
            <span className="hidden md:inline font-semibold">Buscar</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
